/**
 * CoverageAudit
 *
 * Superadmin Coverage Audit — encounter listing.
 *
 * The page audits the transcript→summary conversion, so the default filter
 * is `status=completed` (transcript + summary both persisted). Clinicians
 * can opt into `in_progress` (transcript only) or `all`.
 *
 * Server-side paginated + filtered. The page owns URL state (page, status,
 * search, withIssues, dateFrom, dateTo) so deep links and refreshes are
 * stable. The store holds the current page + total + loading state.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useCoverageAuditStore,
  type CoverageAuditStatusFilter,
  type EncounterRow,
} from '../stores/coverageAuditStore';

// ----------------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------------

const PAGE_SIZE = 50;

const STATUS_OPTIONS: ReadonlyArray<{
  value: CoverageAuditStatusFilter;
  label: string;
}> = [
  { value: 'completed', label: 'Completed' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'all', label: 'All' },
];

// ----------------------------------------------------------------------------
// URL-state helpers
// ----------------------------------------------------------------------------

interface PageFilters {
  page: number;
  status: CoverageAuditStatusFilter;
  search: string;
  withIssues: boolean;
  dateFrom: string;
  dateTo: string;
}

function readFilters(qs: URLSearchParams): PageFilters {
  const page = parseInt(qs.get('page') ?? '1', 10);
  const statusRaw = qs.get('status');
  const status: CoverageAuditStatusFilter =
    statusRaw === 'in_progress' || statusRaw === 'all'
      ? statusRaw
      : 'completed';
  return {
    page: Number.isFinite(page) && page > 0 ? page : 1,
    status,
    search: qs.get('search') ?? '',
    withIssues: qs.get('withIssues') === 'true',
    dateFrom: qs.get('dateFrom') ?? '',
    dateTo: qs.get('dateTo') ?? '',
  };
}

function writeFilters(f: PageFilters): URLSearchParams {
  const next = new URLSearchParams();
  if (f.page !== 1) next.set('page', String(f.page));
  if (f.status !== 'completed') next.set('status', f.status);
  if (f.search) next.set('search', f.search);
  if (f.withIssues) next.set('withIssues', 'true');
  if (f.dateFrom) next.set('dateFrom', f.dateFrom);
  if (f.dateTo) next.set('dateTo', f.dateTo);
  return next;
}

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------

export default function CoverageAudit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);

  const encounters = useCoverageAuditStore((s) => s.encounters);
  const pagination = useCoverageAuditStore((s) => s.encountersPagination);
  const loading = useCoverageAuditStore((s) => s.encountersLoading);
  const error = useCoverageAuditStore((s) => s.encountersError);
  const loadEncounters = useCoverageAuditStore((s) => s.loadEncounters);

  // Local-only mirror of the search box so typing doesn't fire a request
  // per keystroke. Submit on Enter / blur, or after a short debounce.
  const [searchInput, setSearchInput] = useState<string>(filters.search);
  useEffect(() => setSearchInput(filters.search), [filters.search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== filters.search) {
        applyFilter({ search: searchInput, page: 1 });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const applyFilter = useCallback(
    (patch: Partial<PageFilters>) => {
      setSearchParams(writeFilters({ ...filters, ...patch }));
    },
    [filters, setSearchParams],
  );

  // Re-fetch whenever URL filters change.
  useEffect(() => {
    void loadEncounters({
      page: filters.page,
      limit: PAGE_SIZE,
      status: filters.status,
      search: filters.search || undefined,
      withIssues: filters.withIssues || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
    });
  }, [
    loadEncounters,
    filters.page,
    filters.status,
    filters.search,
    filters.withIssues,
    filters.dateFrom,
    filters.dateTo,
  ]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <header className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Coverage Audit</h1>
          <p className="text-sm text-gray-500 mt-1">
            Audit the transcript→summary conversion. Default view shows
            encounters with both a transcript and a generated summary.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/management/coverage-audit/stages"
            className="text-sm px-3 py-1.5 rounded border border-purple-200 bg-purple-50 text-purple-800 hover:bg-purple-100 font-semibold"
          >
            Stage dashboard →
          </Link>
          <Link
            to="/management/coverage-audit/model-inspector"
            className="text-sm px-3 py-1.5 rounded border border-purple-200 bg-purple-50 text-purple-800 hover:bg-purple-100 font-semibold"
          >
            Model inspector →
          </Link>
        </div>
      </header>

      <FiltersBar
        filters={filters}
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onChange={applyFilter}
        totalShown={encounters.length}
        total={pagination.total}
      />

      <div className="bg-white border rounded mt-3 overflow-hidden">
        {loading ? (
          <SkeletonRows count={Math.min(8, PAGE_SIZE)} />
        ) : error ? (
          <ErrorState
            error={error}
            onRetry={() =>
              void loadEncounters({
                page: filters.page,
                limit: PAGE_SIZE,
                status: filters.status,
                search: filters.search || undefined,
                withIssues: filters.withIssues || undefined,
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
              })
            }
          />
        ) : encounters.length === 0 ? (
          <EmptyState filters={filters} />
        ) : (
          <EncounterTable rows={encounters} />
        )}
      </div>

      {!loading && !error && pagination.total > 0 && (
        <Pager
          page={pagination.page}
          pages={pagination.pages}
          total={pagination.total}
          limit={pagination.limit}
          onChange={(page) => applyFilter({ page })}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Filters bar
// ----------------------------------------------------------------------------

interface FiltersBarProps {
  filters: PageFilters;
  searchInput: string;
  onSearchInput: (s: string) => void;
  onChange: (patch: Partial<PageFilters>) => void;
  totalShown: number;
  total: number;
}

function FiltersBar({
  filters,
  searchInput,
  onSearchInput,
  onChange,
  totalShown,
  total,
}: FiltersBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        type="search"
        value={searchInput}
        onChange={(e) => onSearchInput(e.target.value)}
        placeholder="Search by patient name…"
        className="flex-1 min-w-[240px] px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
      />

      <div className="inline-flex rounded border bg-white text-xs overflow-hidden">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange({ status: opt.value, page: 1 })}
            className={`px-3 py-2 ${
              filters.status === opt.value
                ? 'bg-purple-600 text-white'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <label className="inline-flex items-center gap-2 text-xs text-gray-700 px-3 py-2 border rounded bg-white">
        <input
          type="checkbox"
          checked={filters.withIssues}
          onChange={(e) =>
            onChange({ withIssues: e.target.checked, page: 1 })
          }
        />
        With open issues
      </label>

      <input
        type="date"
        value={filters.dateFrom ? filters.dateFrom.slice(0, 10) : ''}
        onChange={(e) =>
          onChange({
            dateFrom: e.target.value
              ? new Date(`${e.target.value}T00:00:00`).toISOString()
              : '',
            page: 1,
          })
        }
        className="px-2 py-2 text-xs border rounded"
        title="From date"
      />
      <input
        type="date"
        value={filters.dateTo ? filters.dateTo.slice(0, 10) : ''}
        onChange={(e) =>
          onChange({
            dateTo: e.target.value
              ? new Date(`${e.target.value}T23:59:59`).toISOString()
              : '',
            page: 1,
          })
        }
        className="px-2 py-2 text-xs border rounded"
        title="To date"
      />

      <span className="text-xs text-gray-500 ml-auto">
        Showing {totalShown} of {total}
      </span>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Table
// ----------------------------------------------------------------------------

function EncounterTable({ rows }: { rows: EncounterRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <th className="py-2 px-3 font-medium">Encounter date</th>
            <th className="py-2 px-3 font-medium">Patient</th>
            <th className="py-2 px-3 font-medium">Type</th>
            <th className="py-2 px-3 font-medium">Source</th>
            <th className="py-2 px-3 font-medium">Status</th>
            <th className="py-2 px-3 font-medium text-right">Open issues</th>
            <th className="py-2 px-3 font-medium" aria-hidden />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <EncounterListRow key={r.clinicalEncounterId} row={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EncounterListRow({ row }: { row: EncounterRow }) {
  const detailHref = `/management/coverage-audit/encounter/${row.clinicalEncounterId}`;
  return (
    <tr className="border-b last:border-b-0 hover:bg-gray-50">
      <td className="py-2 px-3 whitespace-nowrap text-gray-700">
        {formatDate(row.date)}
      </td>
      <td className="py-2 px-3 font-medium text-gray-900">
        {row.patientName ?? <span className="text-gray-400">Unknown</span>}
      </td>
      <td className="py-2 px-3 text-gray-600 text-xs">
        {row.encounterType ?? '—'}
      </td>
      <td className="py-2 px-3">
        <ModeBadge mode={row.mode} />
      </td>
      <td className="py-2 px-3">
        <StatusBadge status={row.status} />
      </td>
      <td className="py-2 px-3 text-right">
        {row.openIssueCount > 0 ? (
          <span className="inline-flex items-center justify-center text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium min-w-[1.5rem]">
            {row.openIssueCount}
          </span>
        ) : (
          <span className="text-xs text-gray-400">0</span>
        )}
      </td>
      <td className="py-2 px-3 text-right">
        <Link
          to={detailHref}
          className="text-purple-600 hover:text-purple-800 hover:underline text-sm font-medium"
        >
          Review →
        </Link>
      </td>
    </tr>
  );
}

// ----------------------------------------------------------------------------
// Badges
// ----------------------------------------------------------------------------

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-800' },
  in_progress: { label: 'In progress', cls: 'bg-amber-100 text-amber-800' },
  no_transcript: { label: 'No transcript', cls: 'bg-gray-100 text-gray-600' },
  unknown: { label: 'Unknown', cls: 'bg-gray-100 text-gray-600' },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_LABELS[status] ?? {
    label: status || 'Unknown',
    cls: 'bg-gray-100 text-gray-700',
  };
  return (
    <span
      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

function ModeBadge({ mode }: { mode: 'connected' | 'standalone' }) {
  const cls =
    mode === 'connected'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-purple-50 text-purple-700 border-purple-200';
  return (
    <span
      className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide ${cls}`}
    >
      {mode === 'connected' ? 'EHR' : 'Standalone'}
    </span>
  );
}

// ----------------------------------------------------------------------------
// Pager
// ----------------------------------------------------------------------------

function Pager({
  page,
  pages,
  total,
  limit,
  onChange,
}: {
  page: number;
  pages: number;
  total: number;
  limit: number;
  onChange: (page: number) => void;
}) {
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return (
    <div className="flex items-center justify-between mt-3 text-xs text-gray-600">
      <div>
        Showing <span className="font-medium">{start}</span>–
        <span className="font-medium">{end}</span> of{' '}
        <span className="font-medium">{total}</span>
      </div>
      <div className="flex items-center gap-1">
        <PagerBtn disabled={page <= 1} onClick={() => onChange(1)}>
          « First
        </PagerBtn>
        <PagerBtn disabled={page <= 1} onClick={() => onChange(page - 1)}>
          ‹ Prev
        </PagerBtn>
        <span className="px-3 py-1.5">
          Page <span className="font-medium">{page}</span> of{' '}
          <span className="font-medium">{pages}</span>
        </span>
        <PagerBtn disabled={page >= pages} onClick={() => onChange(page + 1)}>
          Next ›
        </PagerBtn>
        <PagerBtn disabled={page >= pages} onClick={() => onChange(pages)}>
          Last »
        </PagerBtn>
      </div>
    </div>
  );
}

function PagerBtn({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="px-2.5 py-1.5 border rounded bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

// ----------------------------------------------------------------------------
// Loading / empty / error states
// ----------------------------------------------------------------------------

function SkeletonRows({ count }: { count: number }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left border-b bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
          <th className="py-2 px-3 font-medium">Encounter date</th>
          <th className="py-2 px-3 font-medium">Patient</th>
          <th className="py-2 px-3 font-medium">Type</th>
          <th className="py-2 px-3 font-medium">Source</th>
          <th className="py-2 px-3 font-medium">Status</th>
          <th className="py-2 px-3 font-medium text-right">Open issues</th>
          <th className="py-2 px-3 font-medium" aria-hidden />
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: count }).map((_, i) => (
          <tr key={i} className="border-b last:border-b-0">
            {Array.from({ length: 7 }).map((__, j) => (
              <td key={j} className="py-2 px-3">
                <div className="h-3 bg-gray-100 rounded animate-pulse" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EmptyState({ filters }: { filters: PageFilters }) {
  const hasFilters =
    filters.search ||
    filters.withIssues ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.status !== 'completed';
  return (
    <div className="text-sm text-gray-600 p-8 text-center">
      <p className="font-medium">
        {hasFilters
          ? 'No encounters match the current filters.'
          : 'No completed encounters yet.'}
      </p>
      <p className="text-xs text-gray-500 mt-1">
        {hasFilters
          ? 'Try clearing filters or switching to All.'
          : 'Once an encounter is recorded, transcribed, and summarized it will appear here.'}
      </p>
    </div>
  );
}

function ErrorState({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}) {
  return (
    <div className="text-sm text-red-700 bg-red-50 border-b border-red-200 p-3 flex items-start gap-3">
      <span className="font-semibold">Couldn't load encounters.</span>
      <span className="text-red-600 flex-1">{error}</span>
      <button
        type="button"
        className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
        onClick={onRetry}
      >
        Retry
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
